import React from 'react';
import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import RenderDetailField from "containers/common/RenderDetailField";
import "containers/common/Detail.css";
import OwnerPageFileList from "containers/owner/page/OwnerPageFileList";
import OwnerPageOriginList from "containers/owner/page/OwnerPageOriginList";
import OwnerPageIndexList from "containers/owner/page/OwnerPageIndexList";
import OwnerPageTreeList from "containers/owner/page/OwnerPageTreeList";
import DatePicker from "react-datepicker";
import { Link } from "react-router-dom";
import { s3GetFolderFiles, _s3GetURL, s3Upload } from "lib/awsLib";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory } from "utils/elementIds";

function OwnerPageGeneralInfo({
    ownerData,
    collections,
    handleChange,
    handleBlur,
    handleImmediateSave,
    toggleOwnerPageIndexModal,
    toggleOwnerPageOriginModal,
    toggleOwnerPageTreeModal,
    toggleOwnerPageSearchModal,
    toggleOwnerPageTreeChildModal,
    setUi,
    openAddressSelect,
    saveRecord,
    user,
    updateTableState,
}) {

    const [s3FileUrls, setS3FileUrls] = useState([]);
    const [uploadErrorOccurred, setUploadErrorOccurred] = useState(false);
    const [failedUploadSearchFileName, setFailedUploadSearchFileName] = useState(null);
    const uploadErrorMessage = "There was an error with the upload request. Please contact the team for assistance";
    const { search_file_name } = ownerData;
    const {
        // Primary Identifiers
        owner_id,
        origin_owner_id,
        // origin_db,
        owner_name,
        is_company,

        // Name & Entity info
        date_last_search,
        date_next_search,
        acn,
        dir_name_1,
        dir_name_2,
        search_notes,

        // Physical Address
        exclude_from_mailings,
        care_of,
        address_line_1,
        address_line_2,
        suburb,
        state,
        postcode,
        country,

        // Postal Address
        p_exclude_from_mailings,
        p_care_of,
        p_po_box,
        p_suburb,
        p_state,
        p_postcode,
        p_country,

        // Contact Details
        telephone,
        fax,
        website,
        email,
        contact_salutation,
        contact_first_name,
        contact_middle_initial,
        contact_surname,
        contact_position,
        contact_email,
        contact_telephone,
        contact_notes,

        //Owner Text
        owner_text,
        alternate_text,

        //General Notes
        notes,
    } = ownerData || {};
    const normalizedCountryOptions = useMemo(() => {
        const source = Array.isArray(collections.countryOptions) ? collections.countryOptions : [];

        const mapped = source
            .map((opt) => {
                if (!opt) {
                    return null;
                }

                if (typeof opt === "string") {
                    const text = opt.trim();
                    return text ? { value: text, label: text } : null;
                }

                const value = String(opt.value ?? opt.country ?? opt.country_name ?? opt.name ?? opt.label ?? "").trim();
                if (!value) {
                    return null;
                }

                return {
                    ...opt,
                    value,
                    label: String(opt.label ?? opt.country ?? opt.country_name ?? opt.name ?? value),
                };
            })
            .filter(Boolean);

        const seen = new Set();
        return mapped.filter((opt) => {
            if (seen.has(opt.value)) {
                return false;
            }
            seen.add(opt.value);
            return true;
        });
    }, [collections.countryOptions]);

    const isStreetCountryCustom = country === "(Custom)" || (country && !normalizedCountryOptions.find(opt => opt.value === country));
    const isPostalCountryCustom = p_country === "(Custom)" || (p_country && !normalizedCountryOptions.find(opt => opt.value === p_country));
    const createId = useMemo(() => createElementIdFactory("owner-page-general-info"), []);

    const loadS3Files = useCallback(async () => {
        if (!owner_id || String(owner_id) === "new") {
            setS3FileUrls([]);
            return;
        }

        const fileList = getFileListFromSearchName(search_file_name);

        try {
            const legacyNameOnlyEntries = fileList.filter(f => !String(f?.key || "").trim());

            const expectedKeys = new Set(
                fileList
                    .map(f => String(f?.key || "").trim())
                    .filter(Boolean)
            );

            const expectedLegacyNames = new Set(
                legacyNameOnlyEntries
                    .map(f => String(f?.name || "").trim().toLowerCase())
                    .filter(Boolean)
            );

            const folderResult = await s3GetFolderFiles("public/asic");
            const allItems = Array.isArray(folderResult?.items) ? folderResult.items : [];

            const processedFiles = allItems
                .filter(v => v && v.key)
                .filter((v) => {
                    const fullKey = String(v.key || "");
                    const fileName = fullKey.split("/").pop() || "";
                    const fileNameNoExt = fileName.includes(".")
                        ? fileName.substring(0, fileName.lastIndexOf("."))
                        : fileName;

                    if (expectedKeys.size > 0 && expectedKeys.has(fullKey)) {
                        return true;
                    }

                    if (expectedLegacyNames.size === 0) {
                        return false;
                    }

                    return expectedLegacyNames.has(fileNameNoExt.trim().toLowerCase());
                })
                .filter((v, i, a) => a.findIndex(v2 => v2.key === v.key) === i)
                .sort((a, b) => new Date(a.lastModified) - new Date(b.lastModified));

            const urls = await Promise.all(processedFiles.map(async (f) => {
                const fullKey = String(f.key || "");
                const normalizedKey = fullKey.replace(/^public\//, "");

                let url = null;
                try {
                    url = await _s3GetURL(fullKey);
                } catch (error) {
                    try {
                        url = await _s3GetURL(normalizedKey);
                    } catch (fallbackError) {
                        return null;
                    }
                }

                return {
                    key: fullKey,
                    url,
                };
            }));

            setS3FileUrls(urls.filter(Boolean));
        } catch (err) {
            console.error("S3 Load Error:", err);
            setS3FileUrls([]);
        }
    }, [owner_id, search_file_name]);

    useEffect(() => {
        if (!owner_id || String(owner_id) === "new") {
            setS3FileUrls([]);
            return;
        }

        loadS3Files();
    }, [owner_id, search_file_name, loadS3Files]);

    const handleSelectChange = (val, fieldName) => {
        const newValue = val?.value || "";
        let updatedOwner = { ...ownerData, [fieldName]: newValue };

        if (newValue === "(Custom)") {
            updatedOwner[fieldName] = "";
        }

        handleChange({ target: { name: fieldName, value: updatedOwner[fieldName] } });
        handleImmediateSave(updatedOwner);
    };

    const handleCheckboxChange = (e) => {
        handleChange(e);
    };

    const handleAddressClear = (type) => {
        const streetFields = ['care_of', 'address_line_1', 'address_line_2', 'suburb', 'state', 'postcode', 'country'];
        const postalFields = ['p_care_of', 'p_po_box', 'p_suburb', 'p_state', 'p_postcode', 'p_country'];
        const fieldsToClear = type === 'street' ? streetFields : postalFields;
        const updatedOwner = { ...ownerData };
        fieldsToClear.forEach(field => {
            updatedOwner[field] = "";
        });
        handleImmediateSave(updatedOwner);
    };

    const getFileListFromSearchName = (fileNameStr) => {
        if (!fileNameStr) return [];
        try {
            const parsed = JSON.parse(fileNameStr);
            if (!Array.isArray(parsed)) {
                return [{ name: fileNameStr }];
            }

            return parsed
                .map((entry) => {
                    if (typeof entry === "string") {
                        return { name: entry };
                    }
                    if (!entry || typeof entry !== "object") {
                        return null;
                    }

                    const name = String(entry.name || "").trim();
                    const key = String(entry.key || "").trim();

                    if (!name && !key) {
                        return null;
                    }

                    return {
                        ...entry,
                        ...(name ? { name } : {}),
                        ...(key ? { key } : {}),
                    };
                })
                .filter(Boolean);
        } catch (e) {
            return [{ name: fileNameStr }];
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (uploadErrorOccurred) {
            window.alert(uploadErrorMessage);
            return;
        }

        const existingFiles = getFileListFromSearchName(search_file_name);
        const newFileKey = `public/asic/${file.name}`;
        const newNameNoExt = file.name.substring(0, file.name.lastIndexOf("."));

        if (existingFiles.find(f => String(f?.key || "") === newFileKey || f?.name === newNameNoExt)) {
            alert('File already exists.');
            return;
        }

        try {
            const updatedList = [...existingFiles, { name: newNameNoExt, key: newFileKey }];
            const updatedJson = JSON.stringify(updatedList);

            const saved = await handleImmediateSave({ ...ownerData, search_file_name: updatedJson });

            if (saved === false) {
                setUploadErrorOccurred(true);
                setFailedUploadSearchFileName(updatedJson);
                setUi(prev => ({ ...prev, errorMessage: "", showAlert: false }));
                window.alert(uploadErrorMessage);
            } else {
                await s3Upload(file, 'public/asic');
                handleChange(updatedJson, "search_file_name");
                setUploadErrorOccurred(false);
                setFailedUploadSearchFileName(null);
                window.alert("Upload successful!");
            }
        } catch (err) {
            console.error("Upload Error:", err);
            window.alert(`Upload failed: ${err.message}`);
        } finally {
            event.target.value = '';
        }
    };

    const deleteFile = async (fileToDelete, event) => {
        if (event) event.preventDefault();

        if (!window.confirm(`Are you sure you want to delete this extract?`)) {
            return;
        }

        try {
            const currentFiles = getFileListFromSearchName(search_file_name);
            const rawKey = String(fileToDelete?.key || "").trim();
            const rawName = rawKey.replace("public/asic/", "");
            const nameInDB = rawName.substring(0, rawName.lastIndexOf("."));
            const targetName = String(nameInDB || "").toLowerCase();

            const newFileList = currentFiles.filter((f) => {
                const entryKey = String(f?.key || "").trim();
                const entryName = String(f?.name || "").trim().toLowerCase();

                if (rawKey && entryKey) {
                    return entryKey !== rawKey;
                }

                if (targetName) {
                    return entryName !== targetName;
                }

                return true;
            });
            const updatedValue = newFileList.length === 0 ? "" : JSON.stringify(newFileList);

            const saved = await handleImmediateSave({ ...ownerData, search_file_name: updatedValue });

            if (saved === false) {
                setUi(prev => ({ ...prev, errorMessage: "", showAlert: false }));
                window.alert(uploadErrorMessage);
                return;
            }

            handleChange(updatedValue, "search_file_name");

            if (uploadErrorOccurred && failedUploadSearchFileName !== updatedValue) {
                setUploadErrorOccurred(false);
                setFailedUploadSearchFileName(null);
            }

            alert("File removed from this owner record.");
        } catch (err) {
            console.error("Delete Error:", err);
            alert(`Delete failed: ${err?.message || "Check permissions."}`);
        }
    };

    return (
        <Form.Group as={Row}  >
            <Col sm={12} md={6} lg={3}>
                <Card>
                    <Card.Header>Name </Card.Header>
                    <Card.Body>
                        {/* Primary Identification */}
                        <RenderDetailField
                            id={createId("owner-id")}
                            name="owner_id"
                            label="ID"
                            value={<span>{owner_id && owner_id !== 'new' ? owner_id : ''}</span>}
                        />
                        <RenderDetailField
                            id={createId("origin-owner-id")}
                            name="origin_owner_id"
                            label="Key"
                            value={origin_owner_id}
                            onChange={handleChange}
                            disabled={owner_id && owner_id !== 'new'}
                            readOnly={owner_id && owner_id !== 'new'}
                        />
                        <RenderDetailField
                            id={createId("owner-name")}
                            name="owner_name"
                            label="Name"
                            value={owner_name}
                            onChange={handleChange}
                        />
                        <RenderDetailField
                            id={createId("is-company")}
                            name="is_company"
                            label="Owner is a Company"
                        >
                            <Form.Check.Input
                                type="checkbox"
                                name="is_company"
                                checked={!!is_company}
                                onChange={handleCheckboxChange}
                                className="m-0"
                                style={{ width: '1.2em', height: '1.2em', cursor: 'pointer' }}
                            />
                        </RenderDetailField>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>Street Address</Card.Header>
                    <Card.Body>
                        {/* Mailing Toggle */}
                        <RenderDetailField
                            id={createId("exclude-from-mailings")}
                            name="exclude_from_mailings"
                            label="Exclude From Mailings"
                        >
                            <Form.Check.Input
                                type="checkbox"
                                name="exclude_from_mailings"
                                checked={!!exclude_from_mailings}
                                onChange={handleCheckboxChange}
                                className="m-0"
                                style={{ width: '1.2em', height: '1.2em', cursor: 'pointer' }}
                            />
                        </RenderDetailField>
                        <hr />
                        {/* Street Address Details */}
                        <RenderDetailField id={createId("care-of")} name="care_of" label="Care Of" value={care_of || ''} onChange={handleChange} />
                        <RenderDetailField id={createId("address-line-1")} name="address_line_1" label="Address Line 1" value={address_line_1 || ''} onChange={handleChange} />
                        <RenderDetailField id={createId("address-line-2")} name="address_line_2" label="Address Line 2" value={address_line_2 || ''} onChange={handleChange} />
                        <RenderDetailField
                            id={createId("suburb")}
                            name="suburb"
                            label="Suburb"
                            isSelect={true}
                            options={collections.suburbOptions}
                            value={suburb ? { label: suburb, value: suburb } : null}
                            onChange={(val) => handleSelectChange(val, "suburb")}
                        />
                        <RenderDetailField id={createId("state")} name="state" label="State" value={state} onChange={handleChange} />
                        <RenderDetailField id={createId("postcode")} name="postcode" label="Postcode" value={postcode} onChange={handleChange} />
                        <RenderDetailField
                            id={createId("country-select")}
                            name="country"
                            label="Country"
                            isSelect={!isStreetCountryCustom}
                            options={normalizedCountryOptions.concat([{ value: '(Custom)', label: '(Custom)' }])}
                            value={
                                country && country !== '(Custom)'
                                    ? { label: country, value: country }
                                    : (isStreetCountryCustom ? { label: '(Custom)', value: '(Custom)' } : null)
                            }
                            onChange={(val) => handleSelectChange(val, "country")}
                        />

                        {isStreetCountryCustom && (
                            <RenderDetailField
                                id={createId("country-custom")}
                                name="country"
                                label="Custom Country Name"
                                value={country || ''}
                                onChange={handleChange}
                                placeholder="Type custom country..."
                            />
                        )}

                        <Form.Group as={Row} className="mt-2">
                            <Col sm={6} md={6} lg={4}></Col>
                            <Col className="d-flex align-items-center gap-2">
                                <Button variant="secondary" onClick={() => handleAddressClear('street')} size="sm">Clear </Button>
                                <Button variant="secondary" onClick={openAddressSelect} size="sm">Build from Property</Button>
                            </Col>
                        </Form.Group>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>Postal Address</Card.Header>
                    <Card.Body>
                        <RenderDetailField
                            id={createId("postal-exclude-from-mailings")}
                            name="p_exclude_from_mailings"
                            label="Exclude From Mailings"
                        >
                            <Form.Check.Input
                                type="checkbox"
                                name="p_exclude_from_mailings"
                                checked={!!p_exclude_from_mailings}
                                onChange={handleCheckboxChange}
                                className="m-0"
                                style={{ width: '1.2em', height: '1.2em', cursor: 'pointer' }}
                            />
                        </RenderDetailField>
                        <hr />
                        {/* Postal Address Section */}
                        <RenderDetailField id={createId("postal-care-of")} name="p_care_of" label="Care Of" value={p_care_of} onChange={handleChange} />
                        <RenderDetailField id={createId("postal-po-box")} name="p_po_box" label="PO Box" value={p_po_box} onChange={handleChange} />
                        <RenderDetailField
                            id={createId("postal-suburb")}
                            name="p_suburb"
                            label="Suburb"
                            isSelect={true}
                            options={collections.suburbOptions}
                            value={p_suburb ? { label: p_suburb, value: p_suburb } : null}
                            onChange={(val) => handleSelectChange(val, "p_suburb")}
                        />
                        <RenderDetailField id={createId("postal-state")} name="p_state" label="State" value={p_state} onChange={handleChange} />
                        <RenderDetailField id={createId("postal-postcode")} name="p_postcode" label="Postcode" value={p_postcode} onChange={handleChange} />
                        <RenderDetailField
                            id={createId("postal-country-select")}
                            name="p_country"
                            label="Country"
                            isSelect={!isPostalCountryCustom}
                            options={normalizedCountryOptions.concat([{ value: '(Custom)', label: '(Custom)' }])}
                            value={
                                p_country && p_country !== '(Custom)'
                                    ? { label: p_country, value: p_country }
                                    : (isPostalCountryCustom ? { label: '(Custom)', value: '(Custom)' } : null)
                            }
                            onChange={(val) => handleSelectChange(val, "p_country")}
                        />

                        {isPostalCountryCustom && (
                            <RenderDetailField
                                id={createId("postal-country-custom")}
                                name="p_country"
                                label="Custom Postal Country"
                                value={p_country || ''}
                                onChange={handleChange}
                                placeholder="Type custom country..."
                            />
                        )}

                        <Form.Group as={Row} className="mt-3">
                            <Col sm={6} md={6} lg={4}></Col>
                            <Col>
                                <Button variant="secondary" onClick={() => handleAddressClear('postal')} size="sm"> Clear</Button>
                            </Col>
                        </Form.Group>
                    </Card.Body>
                </Card>
            </Col>

            <Col sm={12} md={6} lg={3}>
                <Card id={createId("contact-details-card")}  >
                    <Card.Header>Contact Details </Card.Header>
                    <Card.Body>
                        {/* Company Contact Details */}
                        <RenderDetailField id={createId("telephone")} name="telephone" label="Telephone" value={telephone} onChange={handleChange} type="tel" />
                        <RenderDetailField id={createId("fax")} name="fax" label="Fax" value={fax} onChange={handleChange} type="tel" />
                        <RenderDetailField id={createId("website")} name="website" label="Website" value={website} onChange={handleChange} />
                        <RenderDetailField id={createId("email")} name="email" label="Email" value={email} onChange={handleChange} type="email" />

                        {/* Individual Contact Person Details */}
                        <RenderDetailField
                            id={createId("contact-salutation")}
                            name="contact_salutation"
                            label="Salutation"
                            isSelect={true}
                            options={collections.salutationOptions}
                            value={ownerData.selected_contact_salutation ||
                                (contact_salutation ? { value: contact_salutation, label: contact_salutation } : null)}
                            onChange={(val) => handleSelectChange(val, "contact_salutation")}
                        />
                        <RenderDetailField id={createId("contact-first-name")} name="contact_first_name" label="First Name" value={contact_first_name} onChange={handleChange} />
                        <RenderDetailField id={createId("contact-middle-initial")} name="contact_middle_initial" label="Middle Initial" value={contact_middle_initial} onChange={handleChange} />
                        <RenderDetailField id={createId("contact-surname")} name="contact_surname" label="Surname" value={contact_surname} onChange={handleChange} />
                        <RenderDetailField
                            id={createId("contact-position")}
                            name="contact_position"
                            label="Position"
                            isSelect={true}
                            options={collections.positionOptions}
                            value={ownerData.selected_contact_position ||
                                (contact_position ? { value: contact_position, label: contact_position } : null)}
                            onChange={(val) => handleSelectChange(val, "contact_position")}
                        />
                        <RenderDetailField id={createId("contact-email")} name="contact_email" label="Contact Email" value={contact_email} onChange={handleChange} type="email" />
                        <RenderDetailField id={createId("contact-telephone")} name="contact_telephone" label="Contact Telephone" value={contact_telephone} onChange={handleChange} type="tel" />

                        {/* Contact Notes */}
                        <RenderDetailField
                            id={createId("contact-notes")}
                            name="contact_notes"
                            label="Contact Notes"
                            value={contact_notes}
                            onChange={handleChange}
                            as="textarea"
                            rows={3}
                            bsPrefix="internal"
                        />
                    </Card.Body>
                </Card>
            </Col>

            <Col sm={12} md={6} lg={3}>
                <Card id={createId("owner-text-card")}  >
                    <Card.Header>Owner Text</Card.Header>
                    <Card.Body>
                        <RenderDetailField
                            id={createId("owner-text")}
                            name="owner_text"
                            label="Owner Text"
                            value={owner_text}
                            onChange={handleChange}
                            as="textarea"
                        />
                        <RenderDetailField
                            id={createId("alternate-text")}
                            name="alternate_text"
                            label="Alternate Text"
                            value={alternate_text}
                            onChange={handleChange}
                            as="textarea"
                            rows={4}
                        />
                    </Card.Body>
                </Card>

                <Card  >
                    <Card.Header>Company Search</Card.Header>
                    <Card.Body>
                        <RenderDetailField
                            id={createId("date-last-search")}
                            name="date_last_search"
                            label="Last Search"
                            value={
                                <DatePicker
                                    selected={date_last_search ? new Date(date_last_search) : null}
                                    onChange={(date) => {
                                        handleChange(date, "date_last_search");
                                        handleImmediateSave({ ...ownerData, date_last_search: date });
                                    }}
                                    className="form-control"
                                    dateFormat="dd/MM/yyyy"
                                />
                            }
                        />
                        <RenderDetailField
                            id={createId("date-next-search")}
                            name="date_next_search"
                            label="Next Search"
                            value={
                                <DatePicker
                                    selected={date_next_search ? new Date(date_next_search) : null}

                                    onChange={(date) => {
                                        handleChange(date, "date_next_search");
                                        handleImmediateSave({ ...ownerData, date_next_search: date });
                                    }}
                                    className="form-control"
                                    dateFormat="dd/MM/yyyy"
                                />
                            }
                        />
                        <RenderDetailField id={createId("acn")} name="acn" label="ACN" value={acn} onChange={handleChange} type="text" />
                        <div>
                            <RenderDetailField
                                id={createId("dir-name-1")}
                                name="dir_name_1"
                                label="Director 1 Name (NZ only)"
                                value={dir_name_1}
                                onChange={handleChange}
                                type="text"
                            />
                            <RenderDetailField
                                id={createId("dir-name-2")}
                                name="dir_name_2"
                                label="Director 2 Name (NZ only)"
                                value={dir_name_2}
                                onChange={handleChange}
                                type="text"
                            />
                        </div>
                        <RenderDetailField
                            id={createId("search-notes")}
                            name="search_notes"
                            label="Search Notes"
                            value={search_notes}
                            onChange={handleChange}
                            options={collections.companySearchNotesOptions}
                            isSelect={false}
                            isClearable={true}
                            as="textarea"
                            rows={1}
                            bsPrefix="internal"
                        />

                        <Form.Group as={Row} className="mt-2">
                            <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                                
                            </Form.Label>
                            <Col className="d-flex align-items-center">
                                <Form.Control
                                    id={createId("search-file-upload")}
                                    name="search_file_upload"
                                    type="file"
                                    onChange={handleFileChange}
                                    disabled={uploadErrorOccurred}
                                    style={{ display: 'none' }}
                                />
                                <label 
                                    id={createId("upload-extract-btn")} 
                                    htmlFor={uploadErrorOccurred ? undefined : createId("search-file-upload")}
                                    onClick={(e) => uploadErrorOccurred && e.preventDefault()}
                                    className="btn btn-sm mb-0 d-inline-flex align-items-center justify-content-center text-white border-0" 
                                    style={{ 
                                        backgroundColor: uploadErrorOccurred ? '#cccccc' : '#6C757D',
                                        cursor: uploadErrorOccurred ? 'not-allowed' : 'pointer',
                                        opacity: uploadErrorOccurred ? 0.6 : 1
                                    }}
                                >
                                    <i className="fa fa-upload mr-2"></i>
                                    Choose File to Upload
                                </label>
                            </Col>
                        </Form.Group>
                        <Card className="mt-3">
                            <Card.Header>Company Search Extract</Card.Header>
                            <Card.Body>
                                <OwnerPageFileList
                                    controlType="list"
                                    data={s3FileUrls}
                                    updateTableState={updateTableState}
                                    saveRecord={saveRecord}
                                    ownerid={owner_id}
                                    deleteFile={deleteFile}
                                />
                            </Card.Body>
                        </Card>
                    </Card.Body>
                </Card>
            </Col>

            <Col sm={12} md={6} lg={3}>
                <Card className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        Owner Index
                    </Card.Header>
                    <Card.Body>
                        <Button id={createId("owner-index-edit-btn")} onClick={() => toggleOwnerPageIndexModal()} size="sm" variant="secondary">
                            Edit
                        </Button>
                        <OwnerPageIndexList
                            controlType="list"
                            data={collections.index}
                            updateTableState={updateTableState}
                            saveRecord={saveRecord}
                            user={user}
                            ownerid={owner_id}
                        />
                    </Card.Body>
                </Card>

                <Card  >
                    <Card.Header> Country of Origin </Card.Header>
                    <Card.Body>
                        <Button id={createId("owner-origin-edit-btn")} onClick={toggleOwnerPageOriginModal} size="sm" variant="secondary" className="mb-3">Edit</Button>
                        <OwnerPageOriginList
                            controlType="list"
                            data={collections.origins}
                            updateTableState={updateTableState}
                            saveRecord={saveRecord}
                            countryOptions={collections.countryOptions}
                            ownerid={owner_id}
                        />
                    </Card.Body>
                </Card>

                <Card  >
                    <Card.Header> Parent Company </Card.Header>
                    <Card.Body>
                        <Button id={createId("owner-tree-edit-btn")} onClick={toggleOwnerPageTreeModal} size="sm" variant="secondary">Edit</Button>{" "}
                        <Button id={createId("search-add-parent-btn")} variant="info" className="owner-detail-action-btn" onClick={toggleOwnerPageSearchModal} type="button" size="sm"> Search + Add Owner as Parent</Button>
                        {ownerData.owner_id_child &&
                            <Link to={{ pathname: `../owners/${ownerData.owner_id_child}`, state: { data: collections.idList, propertyidlist: collections.propertyidlist } }} strict  >
                                <Button size="sm" variant="secondary">
                                    Return to Subsidiary
                                </Button>
                            </Link>
                        }
                        <OwnerPageTreeList
                            controlType="list"
                            data={collections.tree}
                            updateTableState={updateTableState}
                            saveRecord={saveRecord}
                            ownerid={owner_id}
                            propertyidlist={collections.propertyidlist}
                            idList={collections.idList}
                            parentChild="subsidiary"
                            onClose={toggleOwnerPageTreeModal}
                        />
                    </Card.Body>
                </Card>

                {collections.treeChild.length > 0 &&
                    <Card  >
                        <Card.Header> Child Company</Card.Header>
                        <Card.Body>
                            <Button onClick={toggleOwnerPageTreeChildModal} size="sm" variant="secondary">Edit</Button>{" "}
                            {ownerData.owner_id_parent &&
                                <Link
                                    to={{
                                        pathname: `../owners/${ownerData.owner_id_parent}`,
                                        state: { data: collections.idList, propertyidlist: collections.propertyidlist }
                                    }} strict  >
                                    <Button size="sm" variant="secondary">
                                        Return to Parent
                                    </Button>
                                </Link>
                            }
                            <OwnerPageTreeList
                                controlType="list"
                                data={collections.treeChild}
                                updateTableState={updateTableState}
                                saveRecord={saveRecord}
                                ownerid={owner_id}
                                propertyidlist={collections.propertyidlist}
                                idList={collections.idList}
                                parentChild="parent"
                                onClose={toggleOwnerPageTreeChildModal}
                            />
                        </Card.Body>
                    </Card>
                }

                {/* Notes Card using RenderDetailField */}
                <Card>
                    <Card.Header>Notes</Card.Header>
                    <Card.Body>
                        <RenderDetailField
                            id={createId("notes")}
                            name="notes"
                            label="General Notes"
                            value={notes}
                            onChange={handleChange}
                            as="textarea"
                            bsPrefix="internal"
                        />
                    </Card.Body>
                </Card>

            </Col>
        </Form.Group>
    );
}

export default OwnerPageGeneralInfo;